const API_BASE_URL = '/api'

class DatabaseService {
  // Helper method for API calls
  static async apiCall(endpoint, options = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        let errorMessage = 'API call failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Handle specific HTTP status codes
        if (response.status === 409) {
          throw new Error(`ข้อมูลซ้ำ: ${errorMessage}`);
        } else if (response.status === 400) {
          throw new Error(`ข้อมูลไม่ถูกต้อง: ${errorMessage}`);
        } else if (response.status === 500) {
          throw new Error(`เกิดข้อผิดพลาดในเซิร์ฟเวอร์: ${errorMessage}`);
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  }

  // Battery methods
  static async getAllBatteries() {
    return this.apiCall('/battery-stock');
  }

  static async addBattery(batteryData) {
    return this.apiCall('/battery-stock', {
      method: 'POST',
      body: JSON.stringify(batteryData),
    });
  }

  static async updateBattery(id, batteryData) {
    return this.apiCall(`/battery-stock/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batteryData),
    });
  }

  static async deleteBattery(id) {
    return this.apiCall(`/battery-stock/${id}`, {
      method: 'DELETE',
    });
  }

  // Charger methods
  static async getAllChargers() {
    return this.apiCall('/charger-stock');
  }

  static async addCharger(chargerData) {
    return this.apiCall('/charger-stock', {
      method: 'POST',
      body: JSON.stringify(chargerData),
    });
  }

  static async updateCharger(id, chargerData) {
    return this.apiCall(`/charger-stock/${id}`, {
      method: 'PUT',
      body: JSON.stringify(chargerData),
    });
  }

  static async deleteCharger(id) {
    return this.apiCall(`/charger-stock/${id}`, {
      method: 'DELETE',
    });
  }

  // Customer methods
  static async getAllCustomers() {
    return this.apiCall('/customers');
  }

  static async addCustomer(customerData) {
    return this.apiCall('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  static async updateCustomer(id, customerData) {
    return this.apiCall(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  static async deleteCustomer(id) {
    return this.apiCall(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // Sales methods
  static async getAllSales() {
    return this.apiCall('/sales');
  }

  // Refactored: fetch via API instead of executing raw SQL
  static async getSales() {
    try {
      return await this.apiCall('/sales');
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error;
    }
  }

  static async updateSalePhones(saleId, customerPhone, additionalPhone) {
    try {
      // ใช้ API มาตรฐานในการอัปเดตข้อมูลใบขาย แทนการรัน SQL โดยตรง
      const payload = {
        customer_phone: customerPhone,
        additional_phone: additionalPhone,
        updatedAt: new Date().toISOString(),
      };
      return await this.apiCall(`/sales/${saleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Error updating sale phones:', error);
      throw error;
    }
  }

  static async getSale(id) {
    return this.apiCall(`/sales/${id}`);
  }

  static async addSale(saleData) {
    return this.apiCall('/sales', {
      method: 'POST',
      body: JSON.stringify(saleData),
    });
  }

  static async updateSale(id, saleData) {
    return this.apiCall(`/sales/${id}`, {
      method: 'PUT',
      body: JSON.stringify(saleData),
    });
  }

  static async deleteSale(id) {
    return this.apiCall(`/sales/${id}`, {
      method: 'DELETE',
    });
  }

  // Utility methods
  static generateSaleId() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const time = String(now.getTime()).slice(-6);
    return `SALE${year}${month}${day}${time}`;
  }

  static formatCurrency(amount) {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  }

  static formatDate(date) {
    if (!date) return '-';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '-';
      
      return dateObj.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  static formatDateTime(date) {
    if (!date) return '-';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '-';
      
      return dateObj.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return '-';
    }
  }

  static getStatusBadgeClass(status) {
    switch (status) {
      case 'พร้อมขาย':
        return 'badge bg-success status-available';
      case 'ขายแล้ว':
        return 'badge bg-secondary status-sold';
      case 'ชำรุด':
        return 'badge bg-danger status-damaged';
      case 'รอตรวจสอบ':
        return 'badge bg-warning status-checking';
      default:
        return 'badge bg-light';
    }
  }
}

export default DatabaseService;
export { DatabaseService };